import React, { useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, Copy, CheckCircle2 } from "lucide-react";

export interface GuideStep {
  title: string;
  body?: React.ReactNode;
  copy?: string;    // Optional value to copy (e.g. Redirect URI)
  link?: { label: string; href: string };
}

export interface ProviderGuideProps {
  providerId: string;
  title?: string;
  steps: GuideStep[];
  defaultOpen?: boolean;
}

/**
 * Collapsible numbered step-by-step guide displayed inside every OAuth provider card.
 * Renders 4–8 steps with optional copy fields (redirect URIs) and external console links.
 */
export const ProviderGuide: React.FC<ProviderGuideProps> = ({
  providerId, title = "Setup guide", steps, defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const doCopy = (idx: number, value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  return (
    <div className="rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 overflow-hidden">
      <button
        type="button"
        data-testid={`provider-guide-toggle-${providerId}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-[color:var(--sg-muted-bg)]"
      >
        <span className="flex items-center gap-2 font-semibold text-[color:var(--sg-fg)]">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {title}{" "}
          <span className="text-xs font-normal text-[color:var(--sg-muted)]">
            ({steps.length} steps)
          </span>
        </span>
        <span className="text-xs text-[color:var(--sg-muted)]">
          {open ? "Hide" : "Show"}
        </span>
      </button>
      {open && (
        <ol className="px-4 pt-1 pb-4 space-y-3" data-testid={`provider-guide-${providerId}`}>
          {steps.map((s, i) => (
            <li
              key={i}
              className="grid grid-cols-[24px_1fr] gap-3 items-start text-sm text-[color:var(--sg-fg)]"
              data-testid={`provider-guide-step-${providerId}-${i}`}
            >
              <span className="mt-0.5 h-6 w-6 rounded-full bg-[color:var(--sg-fg)] text-white flex items-center justify-center text-[11px] font-semibold">
                {i + 1}
              </span>
              <div className="min-w-0">
                <div className="font-medium">{s.title}</div>
                {s.body && <div className="mt-1 text-xs text-[color:var(--sg-muted)] leading-relaxed">{s.body}</div>}
                {s.link && (
                  <a
                    href={s.link.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="mt-1.5 inline-flex items-center gap-1 text-xs text-[color:var(--sg-violet)] hover:underline"
                    data-testid={`provider-guide-link-${providerId}-${i}`}
                  >
                    {s.link.label} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {s.copy && (
                  <button
                    type="button"
                    onClick={() => doCopy(i, s.copy!)}
                    className="mt-1.5 w-full font-mono text-[11px] bg-white border border-[color:var(--sg-border)] rounded px-2 py-1.5 flex items-center gap-2 hover:bg-slate-50"
                    data-testid={`provider-guide-copy-${providerId}-${i}`}
                  >
                    <span className="truncate flex-1 text-left">{s.copy}</span>
                    {copiedIdx === i ? (
                      <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" />
                    ) : (
                      <Copy className="h-3 w-3 text-[color:var(--sg-muted)] shrink-0" />
                    )}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

// ---------------- Ready-made guides for the two "legacy" providers ----------------

export const googleGuide = (redirectUri: string): GuideStep[] => [
  {
    title: "Open the Google Cloud Console",
    body: "You need a Google account that will own the app registration.",
    link: { label: "Google Cloud Console", href: "https://console.cloud.google.com/" },
  },
  {
    title: "Create a project (or pick an existing one)",
    body: "APIs & Services → Enabled APIs → make sure YouTube Data API v3 is turned ON.",
  },
  {
    title: "Configure the OAuth consent screen",
    body: "OAuth consent screen → External → fill app name, support email, publish to Testing (add your email as a test user) or Production.",
  },
  {
    title: "Create OAuth 2.0 credentials",
    body: "APIs & Services → Credentials → Create → OAuth client ID → Web application. Add the exact Authorised redirect URI below.",
    copy: redirectUri,
  },
  {
    title: "Add the required scopes",
    body: "youtube.readonly + youtube.force-ssl (needed for comment replies). Save.",
  },
  {
    title: "Paste Client ID + Secret into SociBaba",
    body: "Copy the values from the console into the fields above and click Save credentials.",
  },
];

export const metaGuide = (redirectUri: string): GuideStep[] => [
  {
    title: "Open the Meta for Developers portal",
    body: "You'll need a Meta / Facebook account with admin rights to a Business Page or Instagram Business account.",
    link: { label: "Meta for Developers → My Apps", href: "https://developers.facebook.com/apps/" },
  },
  {
    title: "Create a new app (Business type)",
    body: "Choose Business → give it a name (e.g. \"SociBaba prod\"). Attach a Meta Business account if you have one.",
  },
  {
    title: "Add Facebook Login for Business + Instagram Graph API",
    body: "Dashboard → Add products. Under Facebook Login → Settings, add the Valid OAuth Redirect URI below (exact match, https, no trailing slash).",
    copy: redirectUri,
  },
  {
    title: "Set the App Domains + Privacy Policy URL",
    body: "Settings → Basic → App Domains: socibaba.com (or your custom domain). Add a Privacy Policy URL and Terms of Service URL — Meta requires both to publish.",
  },
  {
    title: "Request Advanced Access for the required permissions",
    body: (
      <>
        App Review → Permissions and Features → request Advanced Access for
        <code className="mx-1 rounded bg-white px-1 py-0.5 text-[11px]">pages_show_list</code>,
        <code className="mx-1 rounded bg-white px-1 py-0.5 text-[11px]">pages_read_engagement</code>,
        <code className="mx-1 rounded bg-white px-1 py-0.5 text-[11px]">pages_manage_posts</code>,
        <code className="mx-1 rounded bg-white px-1 py-0.5 text-[11px]">instagram_basic</code>,
        <code className="mx-1 rounded bg-white px-1 py-0.5 text-[11px]">instagram_manage_comments</code>,
        <code className="mx-1 rounded bg-white px-1 py-0.5 text-[11px]">instagram_content_publish</code>.
        Each requires a screencast + business verification.
      </>
    ),
  },
  {
    title: "Flip the app to Live",
    body: (
      <>
        Top-right toggle → <b>Live</b>. If you see <i>&quot;Feature unavailable: Facebook Login is currently
        unavailable for this app…&quot;</i> that means either (a) the app is still in Development mode,
        (b) Business Verification is pending, or (c) Data Use Checkup expired. Fix under App Review → Requests.
      </>
    ),
    link: { label: "Meta App Review checklist", href: "https://developers.facebook.com/docs/app-review/" },
  },
  {
    title: "Copy App ID + App Secret into SociBaba",
    body: "Settings → Basic → App ID and App Secret. Paste above and Save. Then test Connect from /platforms.",
  },
];

// Compact guide for the generic OAuth 2.0 providers (LinkedIn, Pinterest, X, etc.)
export const genericProviderGuide = (
  providerId: string,
  consoleLabel: string,
  consoleUrl: string,
  redirectUri: string,
  scopes: string[],
): GuideStep[] => [
  {
    title: `Open the ${consoleLabel}`,
    body: `You'll register a new OAuth 2.0 client here.`,
    link: { label: consoleLabel, href: consoleUrl },
  },
  {
    title: "Create a new OAuth 2.0 app",
    body: "Give it a name, category (Business/Productivity), and a support email.",
  },
  {
    title: "Add the redirect URI (exact match)",
    body: "Copy this into the app's Redirect / Callback URL field. No trailing slash, must be https.",
    copy: redirectUri,
  },
  {
    title: "Request the scopes SociBaba needs",
    body: (
      <>Required scopes: {scopes.map((s) => (
        <code key={s} className="mx-0.5 rounded bg-white px-1 py-0.5 text-[11px]">{s}</code>
      ))}</>
    ),
  },
  {
    title: "Copy Client ID + Client Secret",
    body: "Paste both into the fields above. The secret is stored encrypted (Fernet) in SociBaba.",
  },
  {
    title: "Enable & test Connect",
    body: `Save credentials, set status to Live above, then head to /platforms and click Connect ${providerId} to complete the OAuth handshake as a user.`,
  },
];
